import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { ChefHat, Search, X, Heart, Clock, Users, Plus, Filter } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import RecipeCard from '@/components/recipes/RecipeCard';
import RecipeDetail from '@/components/recipes/RecipeDetail';
import CreateRecipe from '@/components/recipes/CreateRecipe';

export default function Recipes() {
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    health_tags: [],
    category: null
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await base44.auth.me();
        setUser(currentUser);
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  const { data: recipes = [] } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => base44.entities.Recipe.list('-created_date'),
  });

  const { data: favorites = [] } = useQuery({
    queryKey: ['recipe-favorites', user?.id],
    queryFn: () => base44.entities.RecipeFavorite.filter({ user_id: user?.id }),
    enabled: !!user?.id,
  });

  const createRecipeMutation = useMutation({
    mutationFn: (data) => base44.entities.Recipe.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      setShowCreate(false);
      toast.success('Receta creada exitosamente');
    },
  });

  const toggleFavoriteMutation = useMutation({
    mutationFn: async (recipeId) => {
      const existing = favorites.find(f => f.recipe_id === recipeId);
      if (existing) {
        await base44.entities.RecipeFavorite.delete(existing.id);
        return { action: 'removed' };
      } else {
        await base44.entities.RecipeFavorite.create({
          recipe_id: recipeId,
          user_id: user.id
        });
        return { action: 'added' };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['recipe-favorites'] });
      toast.success(result.action === 'added' ? 'Receta guardada' : 'Receta eliminada de favoritos');
    },
  });

  const filteredRecipes = recipes.filter(recipe => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTitle = recipe.title?.toLowerCase().includes(query);
      const matchesIngredients = recipe.ingredients?.some(ing => 
        ing.toLowerCase().includes(query)
      );
      if (!matchesTitle && !matchesIngredients) return false;
    }

    // Health tags filter
    if (selectedFilters.health_tags.length > 0) {
      const hasTag = selectedFilters.health_tags.some(tag => 
        recipe.health_tags?.includes(tag)
      );
      if (!hasTag) return false;
    }

    // Category filter
    if (selectedFilters.category && recipe.category !== selectedFilters.category) {
      return false;
    }

    return true;
  });

  const isFavorite = (recipeId) => {
    return favorites.some(f => f.recipe_id === recipeId);
  };

  const healthTags = [
    { id: 'diabetes', label: 'Diabetes', icon: '🩺' },
    { id: 'hipertension', label: 'Hipertensión', icon: '💓' },
    { id: 'celiaco', label: 'Sin TACC', icon: '🌾' },
    { id: 'vegetariano', label: 'Vegetariano', icon: '🥗' },
    { id: 'vegano', label: 'Vegano', icon: '🌱' },
    { id: 'bajo_sodio', label: 'Bajo Sodio', icon: '🧂' },
    { id: 'bajo_azucar', label: 'Bajo Azúcar', icon: '🍬' },
  ];

  const categories = [
    { id: 'desayuno', label: 'Desayuno', icon: '🌅' },
    { id: 'almuerzo', label: 'Almuerzo', icon: '🍽️' },
    { id: 'cena', label: 'Cena', icon: '🌙' },
    { id: 'snack', label: 'Snack', icon: '🍿' },
    { id: 'postre', label: 'Postre', icon: '🍰' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-gray-50 pb-24 overflow-x-hidden">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40 safe-header">
        <div className="max-w-2xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-500/20">
                <ChefHat className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900 text-base sm:text-lg">Recetas</h1>
                <p className="text-[10px] sm:text-xs text-gray-400 hidden xs:block">Inspírate y cocina saludable</p>
              </div>
            </div>
            <Button onClick={() => setShowCreate(true)} size="sm" className="bg-green-600 hover:bg-green-700 h-8 px-2 sm:px-3">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Nueva</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por receta o ingrediente..."
              className="pl-10 h-12 rounded-xl border-gray-200 bg-white"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
        </motion.div>

        {/* Health Tags Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <p className="text-sm font-medium text-gray-700">Filtrar por salud</p>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {healthTags.map(tag => (
              <button
                key={tag.id}
                onClick={() => {
                  setSelectedFilters(prev => ({
                    ...prev,
                    health_tags: prev.health_tags.includes(tag.id)
                      ? prev.health_tags.filter(t => t !== tag.id)
                      : [...prev.health_tags, tag.id]
                  }));
                }}
                className={`
                  flex items-center gap-1 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all
                  ${selectedFilters.health_tags.includes(tag.id)
                    ? 'bg-green-100 text-green-800 border-2 border-green-500'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                <span>{tag.icon}</span>
                <span>{tag.label}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-6"
        >
          <div className="flex gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setSelectedFilters(prev => ({ ...prev, category: null }))}
              className={`
                px-4 py-2 rounded-xl text-sm whitespace-nowrap transition-all
                ${!selectedFilters.category
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              Todas
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedFilters(prev => ({ ...prev, category: cat.id }))}
                className={`
                  flex items-center gap-1 px-4 py-2 rounded-xl text-sm whitespace-nowrap transition-all
                  ${selectedFilters.category === cat.id
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Recipes Grid */}
        <div className="grid gap-4">
          <AnimatePresence>
            {filteredRecipes.map((recipe, index) => (
              <RecipeCard
                key={recipe.id}
                recipe={recipe}
                isFavorite={isFavorite(recipe.id)}
                onToggleFavorite={() => toggleFavoriteMutation.mutate(recipe.id)}
                onClick={() => setSelectedRecipe(recipe)}
                delay={index * 0.05}
              />
            ))}
          </AnimatePresence>
        </div>

        {filteredRecipes.length === 0 && (
          <div className="text-center py-12">
            <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No se encontraron recetas</p>
          </div>
        )}
      </main>

      {selectedRecipe && (
        <RecipeDetail
          recipe={selectedRecipe}
          isFavorite={isFavorite(selectedRecipe.id)}
          onToggleFavorite={() => toggleFavoriteMutation.mutate(selectedRecipe.id)}
          onClose={() => setSelectedRecipe(null)}
        />
      )}

      {showCreate && (
        <CreateRecipe
          user={user}
          onSave={(data) => createRecipeMutation.mutate(data)}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}